import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, Logo } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const SIDE = "https://images.unsplash.com/photo-1641849461096-7e796050f12b?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000";

function Shell({ title, sub, children }) {
  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 py-10 sm:px-12">
        <Link to="/" className="mb-10" data-testid="auth-logo-link"><Logo /></Link>
        <div className="w-full max-w-md rise">
          <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">{title}</h1>
          {sub && <p className="mt-2 text-slate-600">{sub}</p>}
          <div className="mt-8">{children}</div>
        </div>
      </div>
      <div className="relative hidden lg:block">
        <img src={SIDE} alt="Friends jumping at sunset" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
        <p className="absolute bottom-10 left-10 max-w-sm font-display text-2xl font-bold text-white">One place for the people, the plan and the money.</p>
      </div>
    </div>
  );
}

function GoogleButton({ next }) {
  const { googleLogin } = useAuth();
  return (
    <Button type="button" variant="outline" className="h-11 w-full rounded-full" onClick={() => googleLogin(next)} data-testid="google-login-button">
      <svg className="mr-2 h-4 w-4" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z" /><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.2-.1-2.3-.4-3.5z" /></svg>
      Continue with Google
    </Button>
  );
}

const Divider = () => <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-wider text-slate-400"><span className="h-px flex-1 bg-slate-200" />or<span className="h-px flex-1 bg-slate-200" /></div>;

export function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get("next") || "/dashboard";
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await login(f.email, f.password); nav(next); } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Welcome back" sub="Sign in to your trips.">
      {sp.get("error") === "google" && <p className="mb-4 rounded-xl bg-rose-50 p-3 text-sm text-rose-700" data-testid="google-error">Google sign-in didn't complete. Please try again.</p>}
      <GoogleButton next={next} />
      <Divider />
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email" htmlFor="email"><Input id="email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="h-11 rounded-xl" data-testid="login-email-input" /></Field>
        <Field label="Password" htmlFor="password"><Input id="password" type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="h-11 rounded-xl" data-testid="login-password-input" /></Field>
        <div className="text-right"><Link to="/forgot-password" className="text-sm font-semibold text-orange-600 hover:underline" data-testid="forgot-password-link">Forgot password?</Link></div>
        <Button type="submit" disabled={busy} className="h-11 w-full rounded-full" data-testid="login-submit-button">{busy ? "Signing in…" : "Sign in"}</Button>
      </form>
      <p className="mt-6 text-sm text-slate-600">New to Triply? <Link to={`/register?next=${encodeURIComponent(next)}`} className="font-semibold text-orange-600 hover:underline" data-testid="go-register-link">Create an account</Link></p>
    </Shell>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get("next") || "/dashboard";
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await register(f.name, f.email, f.password); nav(next); } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Start planning free" sub="Unlimited trips. No card needed.">
      <GoogleButton next={next} />
      <Divider />
      <form onSubmit={submit} className="space-y-4">
        <Field label="Your name" htmlFor="name"><Input id="name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="h-11 rounded-xl" data-testid="register-name-input" /></Field>
        <Field label="Email" htmlFor="remail"><Input id="remail" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="h-11 rounded-xl" data-testid="register-email-input" /></Field>
        <Field label="Password" htmlFor="rpw" hint="At least 8 characters."><Input id="rpw" type="password" minLength={8} required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="h-11 rounded-xl" data-testid="register-password-input" /></Field>
        <Button type="submit" disabled={busy} className="h-11 w-full rounded-full" data-testid="register-submit-button">{busy ? "Creating…" : "Create account"}</Button>
        <p className="text-xs text-slate-500">By continuing you agree to our <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy policy</Link>.</p>
      </form>
      <p className="mt-6 text-sm text-slate-600">Already have an account? <Link to={`/login?next=${encodeURIComponent(next)}`} className="font-semibold text-orange-600 hover:underline" data-testid="go-login-link">Sign in</Link></p>
    </Shell>
  );
}

export function ForgotPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/auth/forgot-password", { email }); setSent(data.message); } catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <Shell title="Reset your password" sub="We'll email you a secure link that expires in 1 hour.">
      {sent ? <p className="rounded-xl bg-teal-50 p-4 text-sm text-teal-800" data-testid="forgot-success">{sent}</p> : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="Email" htmlFor="femail"><Input id="femail" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 rounded-xl" data-testid="forgot-email-input" /></Field>
          <Button type="submit" className="h-11 w-full rounded-full" data-testid="forgot-submit-button">Send reset link</Button>
        </form>
      )}
      <p className="mt-6 text-sm"><Link to="/login" className="font-semibold text-orange-600" data-testid="back-to-login">Back to sign in</Link></p>
    </Shell>
  );
}

export function ResetPage() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/reset-password", { token: sp.get("token"), password: pw }); toast.success("Password updated. Please sign in."); nav("/login"); } catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <Shell title="Choose a new password">
      <form onSubmit={submit} className="space-y-4">
        <Field label="New password" htmlFor="npw" hint="At least 8 characters."><Input id="npw" type="password" minLength={8} required value={pw} onChange={(e) => setPw(e.target.value)} className="h-11 rounded-xl" data-testid="reset-password-input" /></Field>
        <Button type="submit" className="h-11 w-full rounded-full" data-testid="reset-submit-button">Update password</Button>
      </form>
    </Shell>
  );
}
